import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Input, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Role2 } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { FilterViewer } from '../../../elements';

const { Text } = Typography;

interface Permission2 {
  id: string;
  target: string;
  targetLabel?: string;
  effect: 'ALLOW' | 'DENY';
  filterGroup?: any;
  filterGroupDetails?: any;
}

interface PaginatedPermissionsResponse {
  data: Permission2[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

interface RolePermissionsListProps {
  role: Role2;
  visible: boolean;
  onClose: () => void;
}

export function RolePermissionsList({ role, visible, onClose }: RolePermissionsListProps) {
  const [permissions, setPermissions] = useState<Permission2[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Virtual scrolling constants
  const ITEM_HEIGHT = 53; // Approximate height of each row
  const BUFFER_SIZE = 10; // Number of items to render beyond visible area
  const MAX_DOM_ITEMS = 100; // Maximum items to keep in DOM

  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleEndIndex, setVisibleEndIndex] = useState(MAX_DOM_ITEMS);

  // Load permissions
  const loadPermissions = useCallback(
    async (pageNum: number, searchTerm: string, append = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await apiClient.get<PaginatedPermissionsResponse>(
          `authorization/roles/${role.id}/permissions`,
          {
            params: {
              page: pageNum,
              pageSize: 50,
              search: searchTerm,
            },
          },
        );

        if (append) {
          setPermissions((prev) => [...prev, ...response.data.data]);
        } else {
          setPermissions(response.data.data);
          setVisibleStartIndex(0);
          setVisibleEndIndex(MAX_DOM_ITEMS);
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }

        setTotal(response.data.total);
        setHasMore(response.data.hasMore);
        setPage(pageNum);
      } catch (error) {
        apiErrorMessage(error);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [role.id],
  );

  // Initialize on modal open
  useEffect(() => {
    if (visible) {
      setSearch('');
      setSearchInput('');
      setPage(1);
      setPermissions([]);
      loadPermissions(1, '');
    }
  }, [visible, loadPermissions]);

  // Handle search
  const handleSearch = useCallback(() => {
    setSearch(searchInput);
    setPage(1);
    setPermissions([]);
    loadPermissions(1, searchInput);
  }, [searchInput, loadPermissions]);

  // Handle scroll for lazy loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop: currentScrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Calculate visible range for virtual scrolling
    const startIndex = Math.max(0, Math.floor(currentScrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
    const endIndex = Math.min(permissions.length, startIndex + MAX_DOM_ITEMS);

    setVisibleStartIndex(startIndex);
    setVisibleEndIndex(endIndex);

    // Load more when near bottom
    if (scrollHeight - currentScrollTop - clientHeight < 200 && hasMore && !loading && !loadingRef.current) {
      loadPermissions(page + 1, search, true);
    }
  }, [permissions.length, hasMore, loading, page, search, loadPermissions]);

  // Get visible permissions for rendering
  const visiblePermissions = permissions.slice(visibleStartIndex, visibleEndIndex);
  const offsetY = visibleStartIndex * ITEM_HEIGHT;
  const totalHeight = permissions.length * ITEM_HEIGHT;

  return (
    <Modal
      title={`${role.name} - Tüm yetkiler (${total})`}
      width="80%"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Kapat
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ marginBottom: '16px' }}>
        <Input
          placeholder="Yetki ara..."
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ maxWidth: '400px' }}
        />
        <Button type="primary" onClick={handleSearch} style={{ marginLeft: '8px' }}>
          Ara
        </Button>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          maxHeight: '500px',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {permissions.length === 0 && !loading ? (
          <Text type="secondary">Bu rolde yetki bulunmamaktadır.</Text>
        ) : (
          <div>
            {/* Table Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(400px, 1fr) minmax(200px, 1fr)',
                gap: '12px',
                padding: '10px 12px',
                background: '#fafafa',
                borderBottom: '2px solid #e8e8e8',
                fontSize: '12px',
                fontWeight: 600,
                color: '#666',
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              <div>Yetki</div>
              <div>Filtre</div>
            </div>

            {/* Virtual scroll container */}
            <div style={{ height: totalHeight, position: 'relative' }}>
              {/* Visible items with offset */}
              <div style={{ transform: `translateY(${offsetY}px)` }}>
                {visiblePermissions.map((permission, index) => {
                  const actualIndex = visibleStartIndex + index;
                  const isAllow = permission.effect === 'ALLOW';
                  let dataSetId = '';
                  if (permission.target.startsWith('dataSets.')) {
                    const parts = permission.target.split('.');
                    dataSetId = parts[1];
                  }

                  return (
                    <div
                      key={permission.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(400px, 1fr) minmax(200px, 1fr)',
                        gap: '12px',
                        padding: '10px 12px',
                        background: actualIndex % 2 === 0 ? 'white' : '#fafafa',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        alignItems: 'center',
                        height: ITEM_HEIGHT,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          style={{
                            color: isAllow ? '#52c41a' : '#ff4d4f',
                            fontWeight: 'bold',
                            fontSize: '16px',
                          }}
                        >
                          {isAllow ? '✓' : '✕'}
                        </span>
                        <span>{permission.targetLabel || permission.target}</span>
                      </div>
                      <div style={{ maxWidth: '400px' }}>
                        {permission.filterGroup && dataSetId !== '' ? (
                          <FilterViewer
                            dataSetId={dataSetId}
                            filterGroup={permission.filterGroup}
                            size="small"
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loading indicator */}
            {loading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Spin />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
